if (!customElements.get("product-info")) {
    customElements.define(
        "product-info",
        class ProductInfo extends HTMLElement {
            quantityInput = undefined;
            quantityForm = undefined;
            onVariantChangeUnsubscriber = undefined;
            cartUpdateUnsubscriber = undefined;
            abortController = undefined;
            pendingRequestUrl = null;
            preProcessHtmlCallbacks = [];
            postProcessHtmlCallbacks = [];

            constructor() {
                super();

                this.quantityInput = this.querySelector(".quantity__input");
            }

            connectedCallback() {
                this.initializeProductSwapUtility();

                this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueChange.bind(this));

                this.initQuantityHandlers();
                this.dispatchEvent(new CustomEvent("product-info:loaded", { bubbles: true }));
            }

            addPreProcessCallback(callback) {
                this.preProcessHtmlCallbacks.push(callback);
            }

            initQuantityHandlers() {
                if (!this.quantityInput) return;

                this.quantityForm = this.querySelector(".product-form__quantity");
                if (!this.quantityForm) return;

                this.setQuantityBoundries();
                if (!this.dataset.originalSection) {
                    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
                }
            }

            disconnectedCallback() {
                this.onVariantChangeUnsubscriber();
                this.cartUpdateUnsubscriber?.();
            }

            initializeProductSwapUtility() {
                this.preProcessHtmlCallbacks.push((html) => html.querySelectorAll(".scroll-trigger").forEach((element) => element.classList.add("scroll-trigger--cancel")));
                this.postProcessHtmlCallbacks.push((newNode) => {
                    window?.Shopify?.PaymentButton?.init();
                    window?.ProductModel?.loadShopifyXR();
                });
            }

            handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
                if (!this.contains(event.target)) return;

                this.resetProductFormState();

                const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
                this.pendingRequestUrl = productUrl;
                const shouldSwapProduct = this.dataset.url !== productUrl;
                const shouldFetchFullPage = this.dataset.updateUrl === "true" && shouldSwapProduct;

                this.renderProductInfo({
                    requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
                    targetId: target.id,
                    callback: shouldSwapProduct ? this.handleSwapProduct(productUrl, shouldFetchFullPage) : this.handleUpdateProductInfo(productUrl),
                });
            }

            resetProductFormState() {
                const productForm = this.productForm;
                productForm?.toggleSubmitButton(true);
                productForm?.handleErrorMessage();
            }

            handleSwapProduct(productUrl, updateFullPage) {
                return (html) => {
                    this.productModal?.remove();

                    const selector = updateFullPage ? "product-info[id^='MainProduct']" : "product-info";
                    const variant = this.getSelectedVariant(html.querySelector(selector));
                    this.updateURL(productUrl, variant?.id);

                    if (updateFullPage) {
                        document.querySelector("head title").innerHTML = html.querySelector("head title").innerHTML;

                        HTMLUpdateUtility.viewTransition(document.querySelector("main"), html.querySelector("main"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
                    } else {
                        HTMLUpdateUtility.viewTransition(this, html.querySelector("product-info"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
                    }
                };
            }

            renderProductInfo({ requestUrl, targetId, callback }) {
                this.abortController?.abort();
                this.abortController = new AbortController();

                fetch(requestUrl, { signal: this.abortController.signal })
                    .then((response) => response.text())
                    .then((responseText) => {
                        this.pendingRequestUrl = null;
                        const html = new DOMParser().parseFromString(responseText, "text/html");
                        callback(html);
                    })
                    .then(() => {
                        // set focus to last clicked option value
                        document.querySelector(`#${targetId}`)?.focus();
                    })
                    .catch((error) => {
                        if (error.name === "AbortError") {
                            console.log("Fetch aborted by user");
                        } else {
                            console.error(error);
                        }
                    });
            }

            getSelectedVariant(productInfoNode) {
                const selectedVariant = productInfoNode.querySelector("variant-selects [data-selected-variant]")?.innerHTML;
                return !!selectedVariant ? JSON.parse(selectedVariant) : null;
            }

            buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
                const params = [];

                !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

                if (optionValues.length) {
                    params.push(`option_values=${optionValues.join(",")}`);
                }

                return `${url}?${params.join("&")}`;
            }

            updateOptionValues(html) {
                const variantSelects = html.querySelector("variant-selects");
                if (variantSelects) {
                    HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
                }
            }

            handleUpdateProductInfo(productUrl) {
                return (html) => {
                    const variant = this.getSelectedVariant(html);

                    this.pickupAvailability?.update(variant);
                    this.updateOptionValues(html);
                    this.updateURL(productUrl, variant?.id);
                    this.updateVariantInputs(variant?.id);
                    this.updatePopupFastOrderVariant(html);

                    if (!variant) {
                        this.setUnavailable();
                        return;
                    }

                    this.updateMedia(html, variant?.featured_media?.id);

                    const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
                        const source = html.getElementById(`${id}-${this.sectionId}`);
                        const destination = this.querySelector(`#${id}-${this.dataset.section}`);
                        if (source && destination) {
                            destination.innerHTML = source.innerHTML;
                            destination.classList.toggle("hidden", shouldHide(source));
                        }
                    };

                    updateSourceFromDestination("price");
                    updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
                    updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");
                    updateSourceFromDestination("Volume");
                    updateSourceFromDestination("Price-Per-Item", ({ classList }) => classList.contains("hidden"));

                    this.updateQuantityRules(this.sectionId, html);
                    this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove("hidden");
                    this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove("hidden");

                    this.productForm?.toggleSubmitButton(
                        html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute("disabled") ?? true,
                        window.variantStrings.soldOut,
                    );

                    publish(PUB_SUB_EVENTS.variantChange, {
                        data: {
                            sectionId: this.sectionId,
                            html,
                            variant,
                        },
                    });
                };
            }

            updatePopupFastOrderVariant(html) {
                const variant = html.querySelector('.fast-order-popup input[name="variant"]').value;
                const variantID = html.querySelector('.fast-order-popup input[name="variant_id"]').value;
                if (variant && variantID) {
                    document.querySelector('.fast-order-popup input[name="variant"]').value = variant;
                    document.querySelector('.fast-order-popup input[name="variant_id"]').value = variantID;
                }
            }

            updateVariantInputs(variantId) {
                this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach((productForm) => {
                    const input = productForm.querySelector('input[name="id"]');
                    input.value = variantId ?? "";
                    input.dispatchEvent(new Event("change", { bubbles: true }));
                });
            }

            updateURL(url, variantId) {
                this.querySelector("share-button")?.updateUrl(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`);

                if (this.dataset.updateUrl === "false") return;
                window.history.replaceState({}, "", `${url}${variantId ? `?variant=${variantId}` : ""}`);
            }

            setUnavailable() {
                this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

                const selectors = ["price", "Inventory", "Sku", "Price-Per-Item", "Volume-Note", "Volume", "Quantity-Rules"]
                    .map((id) => `#${id}-${this.dataset.section}`)
                    .join(", ");
                document.querySelectorAll(selectors).forEach(({ classList }) => classList.add("hidden"));
            }

            updateMedia(html, variantFeaturedMediaId) {
                if (!variantFeaturedMediaId) return;

                const mediaGallerySource = this.querySelector("media-gallery ul");
                const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

                const refreshSourceData = () => {
                    if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
                    const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll("li[data-media-id]"));
                    const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
                    const sourceMap = new Map(mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));
                    return [mediaGallerySourceItems, sourceSet, sourceMap];
                };

                if (mediaGallerySource && mediaGalleryDestination) {
                    let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
                    const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll("li[data-media-id]"));
                    const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
                    let shouldRefresh = false;

                    // add items from new data not present in DOM
                    for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
                        if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
                            mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
                            shouldRefresh = true;
                        }
                    }

                    // remove items from DOM not present in new data
                    for (let i = 0; i < mediaGallerySourceItems.length; i++) {
                        if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
                            mediaGallerySourceItems[i].remove();
                            shouldRefresh = true;
                        }
                    }

                    // refresh
                    if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

                    // if media galleries don't match, sort to match new data order
                    mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
                        const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

                        if (sourceData && sourceData.index !== destinationIndex) {
                            mediaGallerySource.insertBefore(sourceData.item, mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`));

                            // refresh source now that it has been modified
                            [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
                        }
                    });
                }

                // set featured media as active in the media gallery
                this.querySelector(`media-gallery`)?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

                // update media modal
                const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
                const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
                if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
            }

            setQuantityBoundries() {
                const data = {
                    cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
                    min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
                    max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
                    step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
                };

                let min = data.min;
                const max = data.max === null ? data.max : data.max - data.cartQuantity;
                if (max !== null) min = Math.min(min, max);
                if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

                this.quantityInput.min = min;

                if (max) {
                    this.quantityInput.max = max;
                } else {
                    this.quantityInput.removeAttribute("max");
                }
                this.quantityInput.value = min;

                publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
            }

            fetchQuantityRules() {
                const currentVariantId = this.productForm?.variantIdInput?.value;
                if (!currentVariantId) return;

                this.querySelector(".quantity__rules-cart .loading__spinner").classList.remove("hidden");
                return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
                    .then((response) => response.text())
                    .then((responseText) => {
                        const html = new DOMParser().parseFromString(responseText, "text/html");
                        this.updateQuantityRules(this.dataset.section, html);
                    })
                    .catch((e) => console.error(e))
                    .finally(() => this.querySelector(".quantity__rules-cart .loading__spinner").classList.add("hidden"));
            }

            updateQuantityRules(sectionId, html) {
                if (!this.quantityInput) return;
                this.setQuantityBoundries();

                const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
                const selectors = [".quantity__input", ".quantity__rules", ".quantity__label"];
                for (let selector of selectors) {
                    const current = this.quantityForm.querySelector(selector);
                    const updated = quantityFormUpdated.querySelector(selector);
                    if (!current || !updated) continue;
                    if (selector === ".quantity__input") {
                        const attributes = ["data-cart-quantity", "data-min", "data-max", "step"];
                        for (let attribute of attributes) {
                            const valueUpdated = updated.getAttribute(attribute);
                            if (valueUpdated !== null) {
                                current.setAttribute(attribute, valueUpdated);
                            } else {
                                current.removeAttribute(attribute);
                            }
                        }
                    } else {
                        current.innerHTML = updated.innerHTML;
                        if (selector === ".quantity__label") {
                            const updatedAriaLabelledBy = updated.getAttribute("aria-labelledby");
                            if (updatedAriaLabelledBy) {
                                current.setAttribute("aria-labelledby", updatedAriaLabelledBy);
                                // Update the referenced visually hidden element
                                const labelId = updatedAriaLabelledBy;
                                const currentHiddenLabel = document.getElementById(labelId);
                                const updatedHiddenLabel = html.getElementById(labelId);
                                if (currentHiddenLabel && updatedHiddenLabel) {
                                    currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                                }
                            }
                        }
                    }
                }
            }

            get productForm() {
                return this.querySelector(`product-form`);
            }

            get productModal() {
                return document.querySelector(`#ProductModal-${this.dataset.section}`);
            }

            get pickupAvailability() {
                return this.querySelector(`pickup-availability`);
            }

            get variantSelectors() {
                return this.querySelector("variant-selects");
            }

            get relatedProducts() {
                const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), "related-products");
                return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
            }

            get quickOrderList() {
                const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), "quick_order_list");
                return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
            }

            get sectionId() {
                return this.dataset.originalSection || this.dataset.section;
            }
        },
    );
    function clickFastOrder() {
        // Tìm popup và form đặt hàng nhanh
        const productForm = document.querySelector(".product-form");
        if (!productForm) return;

        // Lấy section_id từ data attribute hoặc từ form
        const sectionId =
            productForm.dataset.sectionId ||
            productForm.closest(".shopify-section")?.id?.replace("shopify-section-", "") ||
            document.querySelector('[id^="fast-order-"]')?.id?.replace("fast-order-", "") ||
            "main";

        const popup = document.getElementById(`fast-order-popup-${sectionId}`);
        const form = document.getElementById(`fast-order-form-${sectionId}`);

        if (!popup || !form) return;

        // // Lấy variant ID từ input hidden
        // const variantIdInput = productForm.querySelector('.product-variant-id, [name="id"]');
        // const variantId = variantIdInput?.value || "";

        // // Lấy product title
        // const productTitle =
        //     document.querySelector("h1.product__title")?.textContent?.trim() ||
        //     document.querySelector("[data-product-title]")?.dataset.productTitle ||
        //     form.querySelector('[name="product"]')?.value ||
        //     "";

        // // Lấy giá từ element price
        // const priceElement = document.querySelector(`#price-${sectionId}`) || document.querySelector('[id^="price-"]');
        // let price = "";
        // if (priceElement) {
        //     // Tìm giá sale trước (giá hiển thị khi đang sale)
        //     let priceTextElement = priceElement.querySelector(".price-item--sale.price-item--last");

        //     // Nếu không có giá sale, tìm giá regular (không phải giá cũ)
        //     if (!priceTextElement) {
        //         const regularPriceElements = priceElement.querySelectorAll(".price-item--regular");
        //         for (let el of regularPriceElements) {
        //             // Bỏ qua giá so sánh (có class variant-item__old-price hoặc là thẻ <s>)
        //             if (!el.closest("s") && !el.classList.contains("variant-item__old-price")) {
        //                 priceTextElement = el;
        //                 break;
        //             }
        //         }
        //     }

        //     // Nếu vẫn chưa tìm được, lấy từ toàn bộ element
        //     if (!priceTextElement) {
        //         priceTextElement = priceElement;
        //     }

        //     const priceText = priceTextElement.textContent || priceTextElement.innerText || "";

        //     // Tìm tất cả số trong text (bao gồm dấu phẩy và dấu chấm)
        //     const priceMatches = priceText.match(/[\d,\.]+/g);
        //     if (priceMatches && priceMatches.length > 0) {
        //         // Lấy số cuối cùng (thường là giá hiển thị, không phải giá so sánh)
        //         let priceStr = priceMatches[priceMatches.length - 1];
        //         // Loại bỏ dấu phẩy và dấu chấm (để lấy số thuần)
        //         price = priceStr.replace(/,/g, "").replace(/\./g, "");
        //     }
        // }

        // // Điền giá trị vào form
        // const variantInput = form.querySelector(".variantForm");
        // const priceInput = form.querySelector(".priceForm");
        // const productInput = form.querySelector('[name="product"]');

        // if (variantInput) variantInput.value = variantId;
        // if (priceInput) priceInput.value = price;
        // if (productInput && productTitle) productInput.value = productTitle;

        // Hiển thị popup
        popup.style.display = "flex";
        document.body.classList.add("overflow-hidden");

        // Focus vào input đầu tiên
        setTimeout(() => {
            const firstInput = form.querySelector(".NameCus");
            if (firstInput) firstInput.focus();
        }, 100);
    }

    function closeFastOrderPopup() {
        const popups = document.querySelectorAll(".fast-order-popup");
        popups.forEach((popup) => {
            popup.style.display = "none";
        });
        document.body.classList.remove("overflow-hidden");
    }

    function handlePopupClick(event) {
        // Nếu popup đang bị khóa thì không cho đóng
        const popup = event.currentTarget;
        if (popup?.dataset.locked === "true") return;

        // Chỉ đóng popup khi click trực tiếp vào popup container hoặc overlay
        // Không đóng khi click vào content (event đã được stopPropagation)
        if (event.target.classList.contains("fast-order-popup") || event.target.classList.contains("fast-order-popup__overlay")) {
            closeFastOrderPopup();
        }
    }

    function submitFastOrder(event, sectionId) {
        event.preventDefault();

        const form = document.getElementById(`fast-order-form-${sectionId}`);
        if (!form) return;

        // Lấy các field từ form
        const fName = form.elements["name"];
        const fPhone = form.elements["phone"];

        // Validation
        if (!fName || !fName.value || fName.value.trim() === "") {
            alert("Vui lòng nhập họ tên");
            fName?.focus();
            return;
        }

        if (!fPhone || !fPhone.value || fPhone.value.trim() === "") {
            alert("Vui lòng nhập số điện thoại");
            fPhone?.focus();
            return;
        }

        // Danh sách URL Google Sheets theo tháng
        const d = new Date();
        const listURL = [
            "https://script.google.com/macros/s/AKfycbzUAv4wEGaKEgr42Z7zmbOMlX7H4hfheAOUn7ZJTM4F3b16y1RRK9RgOiyNQpljMztnpw/exec", // Tháng 01-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbwgnEfvmYgYsPhRf73qSF8OcDCGFL3ebVp7pJvRvTAcw6HGWDGnDnpCkvjWvy9kDYWF/exec", // Tháng 02-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbx8m5HmhqyUBTeqZ46RgZTXT0AER9ZlUsI__4Iv_Hh5wKVHY1rL4XbfJFHxeWmrJTUb/exec", // Tháng 03-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbzTU7H5I_LrLxCJ5pZsLmfMZ60fRN0HQKFtiEXJspVz681Eu4LOG4ruWwHr6L-udHtMaQ/exec", // Tháng 04-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbw8OVaaPFFc0Cgyg86LpVHG7SrxzFtC_LjETMqr0T2t8MqTeYIC4tslihVV5Rw8uT4N/exec", // Tháng 05-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbxa3vr3sLxkMXBdv5qCs0KZW4xRXgVI3Kv_UuNqJsA3hp0EGRkrurgR1lCqjJlkts--vw/exec", // Tháng 06-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbwnIh5PG4WRUoo5rtUJGG4w2jmpUZ_yCXeldmH72_J_es476bskCLrC1eGYKlmscV2klg/exec", // Tháng 07-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbwanL_igq5FamOU6UNJfawcit5ZU-LZGApjQvU3M4rrZSLcDFEXaFT6FVwjAiCP-2fR2Q/exec", // Tháng 08-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbyzw7qqIcyHnYPrpzDDtdnOjbttgHt0mPktvh1_54U2_wHcMIQMM16eFtvBWPIe5Cyu/exec", // Tháng 09-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbx6i0kA09vsTDbuJ_04clpJUTv3sgsfEbs1UVNI54Ac8_k5xq4prL0qCcZBHdpsXiMSqA/exec", // Tháng 10-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbzH-ckwf3JeHgDs4Y4nd3vQdxCuxB4CYlA_FR3eshj56B5LCVAxHNKGUmehh0vwgyifYw/exec", // Tháng 11-2026 (dhn)
            "https://script.google.com/macros/s/AKfycbzCqFIM2hXgRv5ChXu9f6NK-cvzn53zn8hEgMoUI78kYxrX_S-vAIg6o0GzVJPyXNJLYQ/exec", // Tháng 12-2026 (dhn)
        ];

        // Chọn URL Google Sheets theo tháng hiện tại
        const scriptURL = listURL[d.getMonth()];
        // const scriptURL = "https://script.google.com/macros/s/AKfycbzbwjy8r7E07DPoxen4yxRxBdHI9WaUUqAWQ1SMStjBB0ANzZIyD5xpev8fSHwFfMa4/exec"; // Stg 2024 (dhn)

        // API endpoint
        const scriptURLAPI = "https://api.zuciani.net/api/web/quick_order";
        // const scriptURLAPI = "https://apistg.zuciani.net/api/web/quick_order";

        // Token
        const token = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJjb25uZWN0X3Nob3BpZnlfaWQiOjF9.4w0MIW0krgg-3BuUSt1dI9UFGDHcLE2ww6Cr8GZnU_M";

        // Tạo FormData từ form
        const dataPost = new FormData(form);
        dataPost.append("token", token);
        dataPost.append("device_post", navigator.userAgent);

        const formData = new FormData(form);
        const data = {};
        formData.forEach((value, key) => {
            data[key] = value;
        });

        // Hiển thị loading (tùy chọn - có thể thêm loading spinner)
        const submitButton = form.querySelector(".SubmitInfo");
        const originalButtonText = submitButton?.textContent;
        if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = "Vui lòng đợi...";
        }

        // Khóa popup: chặn đóng bằng overlay/ESC/nút close
        const popup = document.getElementById(`fast-order-popup-${sectionId}`);
        if (popup) popup.setAttribute("data-locked", "true");
        const closeBtn = popup?.querySelector(".fast-order-popup__close");
        if (closeBtn) closeBtn.disabled = true;

        // Gửi đến Google Sheets trước
        fetch(scriptURL, {
            method: "POST",
            body: new FormData(form),
        })
            .then((response) => response.json())
            .then((res) => {
                console.log("Google Sheets Response:", res);
                // Thêm response từ Google Sheets vào dataPost
                dataPost.append("googlesheet_response", JSON.stringify(res));
                // Đóng popup khi hoàn thành
                closeFastOrderPopup();

                // Sau đó gửi đến API
                return fetch(scriptURLAPI, {
                    method: "POST",
                    body: dataPost,
                });
            })
            .then((apiResponse) => {
                // console.log("API Response:", apiResponse);
                // Hiển thị thông báo thành công
                // alert("Thông tin đã được gửi thành công!\n\nHọ tên: " + fName.value + "\nSố điện thoại: " + fPhone.value);
                form.reset();
                // Restore button
                if (submitButton) {
                    submitButton.disabled = false;
                    submitButton.textContent = originalButtonText;
                }
                // Mở khóa popup
                if (popup) popup.removeAttribute("data-locked");
                if (closeBtn) closeBtn.disabled = false;
            })
            .catch((error) => {
                console.error("Error:", error);
                // Thêm error vào dataPost và vẫn gửi đến API
                dataPost.append("googlesheet_error", error.toString());

                // Vẫn gửi đến API ngay cả khi Google Sheets fail
                fetch(scriptURLAPI, {
                    method: "POST",
                    body: dataPost,
                })
                    .then(() => {
                        alert("Đã gửi thông tin. Vui lòng kiểm tra lại sau.");
                        form.reset();
                        // Đóng popup khi hoàn thành (trong nhánh fallback)
                        closeFastOrderPopup();
                    })
                    .catch((apiError) => {
                        console.error("API Error:", apiError);
                        alert("Có lỗi xảy ra. Vui lòng thử lại.");
                    })
                    .finally(() => {
                        if (submitButton) {
                            submitButton.disabled = false;
                            submitButton.textContent = originalButtonText;
                        }
                        // Mở khóa popup
                        if (popup) popup.removeAttribute("data-locked");
                        if (closeBtn) closeBtn.disabled = false;
                    });
            });
    }

    // Đóng popup khi nhấn ESC
    document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" || event.keyCode === 27) {
            // Nếu có popup đang khóa thì không cho đóng bằng ESC
            const lockedPopup = document.querySelector('.fast-order-popup[data-locked="true"]');
            if (lockedPopup) return;
            closeFastOrderPopup();
        }
    });
}
